import { Injectable, effect, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Breadcrumb } from '../../../shared/models/breadcrumb.model';
import { DataService } from '../data/data.service';

/**
 * Service responsible for automatically generating and managing the application's breadcrumb trail.
 * It listens to router navigation events and constructs a path based on route metadata and
 * active application context.
 */
@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private router = inject(Router);
  private dataService = inject(DataService);

  /** Expose breadcrumb items from DataService as a signal for UI components */
  public breadcrumbItems = this.dataService.breadcrumbItems;

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = (event as NavigationEnd).urlAfterRedirects;
        // Clear settings entity context when navigating to top-level list/form pages
        // (not /details/) so that standalone user/client detail pages don't inherit
        // a stale broker or client context from a previous drill-down.
        if (
          /\/settings\/(users|clients|brokers|parties)/.test(url) &&
          !url.includes('/details/') &&
          !url.includes('/edit/')
        ) {
          this.dataService.setCurrentBroker(null);
          this.dataService.setCurrentClient(null);
        } else if (!url.includes('/settings/')) {
          // Leaving settings entirely also clears the context
          this.dataService.setCurrentBroker(null);
          this.dataService.setCurrentClient(null);
          this.dataService.setCurrentDeclarationTemplate(null);
        } else if (
          url.includes('/settings/') &&
          !url.includes('/declaration-templates/') &&
          !(
            url.includes('/manifest-templates/') &&
            url.includes('declarationTemplateId=')
          )
        ) {
          // Left declaration-template → nested-manifest flow
          this.dataService.setCurrentDeclarationTemplate(null);
        }
        this.updateBreadcrumbs();
      });

    // Re-compute breadcrumbs when dynamic data loads asynchronously after navigation,
    // OR when the active module changes via a footer icon click (no NavigationEnd fires).
    effect(() => {
      this.dataService.currentShipment();
      this.dataService.currentDeclaration();
      this.dataService.activeModule();
      this.dataService.breadcrumbParentModule();
      this.dataService.currentEntityName(); // any detail entity name (broker, client, etc.)
      this.dataService.currentBroker(); // settings broker → child entity drill-down
      this.dataService.currentClient(); // settings client → child entity drill-down
      this.dataService.currentDeclarationTemplate(); // template → nested manifest
      this.dataService.listBreadcrumbContext();
      this.dataService.dashboardNavContext();
      this.updateBreadcrumbs();
    });
  }

  /**
   * Triggers a recalculation of the breadcrumb trail based on the current router state.
   */
  public updateBreadcrumbs(): void {
    const root = this.router.routerState.snapshot.root;
    const url = this.router.url.split('?')[0];

    this.dataService.updateDashboardNavContext(
      url,
      this.router.lastSuccessfulNavigation()?.extras?.state as
        | Record<string, unknown>
        | undefined
    );

    this.captureBreadcrumbParentFromNavigation();

    // Always start with the mandatory Home icon root
    let breadcrumbs: Breadcrumb[] = [
      { id: 0, itemName: 'home', route: '/dashboard', showArrow: true },
    ];

    // Build the rest of the breadcrumbs from the route path
    this.addBreadcrumbs(root, '', breadcrumbs);

    // Handle Contextual Stacking (e.g. Shipment -> Declaration) based on navigation history
    breadcrumbs = this.handleContextualStacking(breadcrumbs, root);

    breadcrumbs = this.injectListBreadcrumbContext(breadcrumbs, url);

    breadcrumbs = this.injectDashboardLandingContext(breadcrumbs, url);

    breadcrumbs = this.injectTemplatesDeletedListContext(breadcrumbs, url);

    breadcrumbs = this.reshapeRtoShipmentDetailsBreadcrumbs(breadcrumbs, url);

    breadcrumbs = this.reshapeEmcsBreadcrumbs(breadcrumbs, url);

    // Final pass for structural integrity: IDs, separators, and specific styling flags
    breadcrumbs = this.finalizeBreadcrumbs(breadcrumbs);

    this.dataService.setBreadcrumbItems(breadcrumbs);
  }

  /**
   * Recursively traverses the route tree to collect breadcrumb metadata.
   * Replaces static labels with dynamic IDs when both are present.
   *
   * @param route The current route snapshot being processed
   * @param url The accumulated URL path so far
   * @param breadcrumbs The array of breadcrumbs being populated
   */
  private addBreadcrumbs(
    route: ActivatedRouteSnapshot,
    url: string,
    breadcrumbs: Breadcrumb[]
  ): void {
    const children = route.children;

    if (children.length === 0) {
      return;
    }

    for (const child of children) {
      const routeURL: string = child.url
        .map((segment) => segment.path)
        .join('/');

      const fullURL = url + (routeURL ? `/${routeURL}` : '');
      // Use routeConfig.data (own data only) to avoid Angular's inherited data merging,
      // which would otherwise propagate the parent's breadcrumb label to all children.
      const ownData = child.routeConfig?.data;
      const breadcrumbData = ownData?.['breadcrumb'];
      const dynamicIdType = ownData?.['dynamicId'];

      if (breadcrumbData || dynamicIdType) {
        // For flat multi-segment routes (e.g. 'brokers/bookmarked' in settings),
        // auto-push the entity grouping segment as an intermediate breadcrumb so
        // the trail reads: globalModules › brokers › bookmarked instead of just
        // globalModules › bookmarked.
        const urlSegments = routeURL.split('/');
        if (urlSegments.length > 1) {
          const entitySegment = urlSegments[0];
          // Skip path-action keywords and numeric IDs — only push entity names.
          const PATH_ACTIONS = [
            'details',
            'edit',
            'new',
            'create',
            'all',
            'archived',
            'bookmarked',
            'alerts',
            'info',
            'tasks',
            'list',
            'board-view',
            'import',
            'delivery-items',
            'last-viewed',
          ];
          const isEntitySegment =
            !PATH_ACTIONS.includes(entitySegment) &&
            !/^\d+$/.test(entitySegment);
          // Convert kebab-case to camelCase for translation key compatibility
          let entityLabel = entitySegment.replace(/-([a-z])/g, (_, l: string) =>
            l.toUpperCase()
          );
          if (entityLabel === 'mawb') {
            entityLabel = 'shipments';
          }
          if (entityLabel === 'declarationTemplates') {
            entityLabel = 'templates';
          }
          const alreadyPresent = breadcrumbs.some(
            (b) => b.itemName === entityLabel
          );
          if (isEntitySegment && !alreadyPresent) {
            breadcrumbs.push({
              id: 0,
              itemName: entityLabel,
              route: this.breadcrumbRoute(entityLabel, url + '/' + entitySegment),
              showArrow: true,
            });
          }
        }

        let label = breadcrumbData;
        // Track whether the final label came from a live dynamic resolution so the
        // header's pageTitle knows NOT to use a plain entity name as the large title.
        let isDynamic = false;

        // If a dynamic ID is specified, attempt to resolve it
        if (dynamicIdType) {
          const dynamicLabel = this.getDynamicLabel(dynamicIdType);
          if (dynamicLabel) {
            // Priority: Dynamic label REPLACES the static breadcrumbData
            label = dynamicLabel;
            isDynamic = true;
          }
        }

        // Add to trail if we have a resolved label
        if (label) {
          breadcrumbs.push({
            id: 0,
            itemName: label,
            route: this.breadcrumbRoute(label, fullURL),
            showArrow: true,
            isDynamic,
          });
        }
      }

      this.addBreadcrumbs(child, fullURL, breadcrumbs);
    }
  }

  /**
   * Route stored on a crumb built from the router tree.
   * The NCTS module root redirects to the arrival list. Point the crumb at that
   * list so the footer arrival and departure tabs stay visible.
   * @param label - Breadcrumb translation key
   * @param fullURL - Path accumulated from the route snapshot
   * @returns Absolute route for the crumb
   */
  private breadcrumbRoute(label: string, fullURL: string): string {
    if (label === 'globalModules') {
      return '/settings/brokers';
    }
    if (label === 'transitNcts' && fullURL === '/transit-declarations') {
      return '/transit-declarations/transit-arrival';
    }
    if (
      label === 'downloadTemplates' &&
      fullURL.startsWith('/settings/download-templates')
    ) {
      return '/settings/declaration-templates/download';
    }
    return fullURL;
  }

  /**
   * Inserts the import/export list crumb when the user opened a detail page from that list.
   * Trail: home › shipments › importShipments › #2233 (and the same pattern for declarations).
   */
  private injectListBreadcrumbContext(
    breadcrumbs: Breadcrumb[],
    url: string
  ): Breadcrumb[] {
    const activeModule = this.dataService.activeModule();
    if (activeModule === 'import' || activeModule === 'export') {
      return breadcrumbs;
    }

    const ctx = this.dataService.listBreadcrumbContext();
    if (!ctx) {
      return breadcrumbs;
    }

    const isShipmentDetails = url.includes('/shipments/details/');
    const isDeclarationDetails = url.includes('/declarations/details/');

    if (ctx.module === 'shipments' && !isShipmentDetails) {
      return breadcrumbs;
    }
    if (ctx.module === 'declarations' && !isDeclarationDetails) {
      return breadcrumbs;
    }

    const moduleKey = ctx.module;
    const moduleIdx = breadcrumbs.findIndex((b) => b.itemName === moduleKey);
    if (moduleIdx === -1) {
      return breadcrumbs;
    }

    if (breadcrumbs.some((b) => b.itemName === ctx.itemName)) {
      return breadcrumbs;
    }

    const staticDetailLabels = new Set([
      'shipmentDetails',
      'declarationsDetails',
    ]);
    const withoutStaticDetail = breadcrumbs.filter(
      (b) => !staticDetailLabels.has(b.itemName)
    );

    const listCrumb: Breadcrumb = {
      id: 0,
      itemName: ctx.itemName,
      route: ctx.route,
      showArrow: true,
    };

    const moduleIdxAdjusted = withoutStaticDetail.findIndex(
      (b) => b.itemName === moduleKey
    );
    if (moduleIdxAdjusted === -1) {
      return breadcrumbs;
    }

    return [
      ...withoutStaticDetail.slice(0, moduleIdxAdjusted + 1),
      listCrumb,
      ...withoutStaticDetail.slice(moduleIdxAdjusted + 1),
    ];
  }

  /**
   * When the user navigated from a module dashboard, inserts a Dashboard crumb
   * between Home and the module list/detail trail (e.g. home › dashboard › shipments).
   */
  private injectDashboardLandingContext(
    breadcrumbs: Breadcrumb[],
    url: string
  ): Breadcrumb[] {
    const ctx = this.dataService.dashboardNavContext();
    if (!ctx) {
      return breadcrumbs;
    }

    const path = url.split('?')[0];

    if (path.includes('/dashboard/')) {
      return breadcrumbs;
    }

    const currentModule = this.getDashboardNavModuleFromPath(path);
    if (!currentModule || currentModule !== ctx.module) {
      return breadcrumbs;
    }

    if (breadcrumbs.some((b) => b.itemName === 'dashboard')) {
      return breadcrumbs;
    }

    const dashboardCrumb: Breadcrumb = {
      id: 0,
      itemName: 'dashboard',
      route: ctx.dashboardRoute,
      showArrow: true,
    };

    return [breadcrumbs[0], dashboardCrumb, ...breadcrumbs.slice(1)];
  }

  private getDashboardNavModuleFromPath(
    path: string
  ): 'shipments' | 'declarations' | 'tickets' | 'settings' | null {
    if (/^\/shipments(?:\/|$)/.test(path)) return 'shipments';
    if (/^\/declarations(?:\/|$)/.test(path)) return 'declarations';
    if (/^\/tickets(?:\/|$)/.test(path)) return 'tickets';
    if (/^\/settings(?:\/|$)/.test(path)) return 'settings';
    return null;
  }

  /**
   * Resolves dynamic labels for breadcrumb trail items.
   *
   * Shipment and Declaration use their own dedicated signals (prefixed with `#`).
   * All other entity types (broker, client, user, ticket, manifestTemplate, …) are
   * resolved from the generic `currentEntityName` registry that detail-page components
   * populate via `DataService.setCurrentEntityName()`.
   *
   * @param type The `dynamicId` value declared on the route's data object
   * @returns The resolved display string, or '' if not yet available
   */
  private getDynamicLabel(type: string): string {
    if (type === 'shipment') {
      const shipment = this.dataService.currentShipment();
      return shipment?.shipmentNo ? `#${shipment.shipmentNo}` : '';
    }
    if (type === 'declaration') {
      const declaration = this.dataService.currentDeclaration();
      return declaration?.lrn ? `#${declaration.lrn}` : '';
    }
    // Generic: any detail page that calls dataService.setCurrentEntityName(type, name)
    return this.dataService.currentEntityName()[type] || '';
  }

  /**
   * Enriches the breadcrumb trail when the user navigates across module boundaries.
   *
   * Two complementary patterns are handled:
   *
   * **Pattern 1 – parentLookup (drill-in):**
   * A route declares `data: { parentLookup: 'shipment' }` to signal that it was reached
   * by drilling into a child entity from a parent module.  When the active module matches,
   * the parent entity's breadcrumb chain is prepended automatically.
   * Add new entity types inside `tryInsertParentTrail` as new `case` blocks.
   *
   * **Pattern 2 – cross-module (lateral navigation):**
   * The user is in Module X but the current URL belongs to Module Y (e.g. clicking a
   * Shipment No. link from the Declarations list).  The host module's root label is
   * prepended and the foreign module's root label is stripped.
   * Add new cases inside `tryInsertCrossModuleContext`.
   */
  private handleContextualStacking(
    breadcrumbs: Breadcrumb[],
    root: ActivatedRouteSnapshot
  ): Breadcrumb[] {
    const activeModule = this.dataService.activeModule();
    const url = this.router.url;

    // Pattern 1: route has parentLookup metadata
    const parentLookupType = this.getRouteMetadataValue(root, 'parentLookup');
    if (parentLookupType) {
      const result = this.tryInsertParentTrail(
        breadcrumbs,
        parentLookupType,
        activeModule
      );
      if (result) return result;
    }

    // Pattern 2: viewing a foreign-module entity from a different active module
    const result = this.tryInsertCrossModuleContext(
      breadcrumbs,
      activeModule,
      url
    );
    if (result) return result;

    return breadcrumbs;
  }

  /**
   * Prepends the parent entity trail when drilling from one module into another.
   * Extend this method with additional `case` blocks to support more entity types.
   */
  private tryInsertParentTrail(
    breadcrumbs: Breadcrumb[],
    parentLookupType: string,
    activeModule: string
  ): Breadcrumb[] | null {
    switch (parentLookupType) {
      case 'shipment': {
        if (
          activeModule !== 'import' &&
          activeModule !== 'export' &&
          activeModule !== 'shipments' &&
          activeModule !== 'shipment-list' &&
          activeModule !== 'declarations'
        )
          return null;

        const ctx = this.dataService.listBreadcrumbContext();
        const decl = this.dataService.currentDeclaration();
        const isExport = decl?.declarationType?.toUpperCase().startsWith('EX');
        const actualModule =
          activeModule === 'import' || activeModule === 'export'
            ? activeModule
            : isExport
            ? 'export'
            : 'import';

        if (ctx?.module === 'declarations') {
          // Import/Export -> Declarations -> LRN
          const home = breadcrumbs[0];
          const parentTrail: Breadcrumb[] = [
            {
              id: 0,
              itemName: actualModule,
              route: `/${actualModule}`,
              showArrow: true,
            },
            {
              id: 0,
              itemName:
                actualModule === 'import'
                  ? 'importDeclarations'
                  : 'exportDeclarations',
              route: `/${actualModule}/declarations`,
              showArrow: true,
            },
          ];
          const remaining = breadcrumbs
            .slice(1)
            .filter(
              (b) =>
                b.itemName !== 'declarations' &&
                b.itemName !== 'importDeclarations' &&
                b.itemName !== 'exportDeclarations'
            );
          return [home, ...parentTrail, ...remaining];
        }

        // Import/Export -> Shipments -> Shipment No -> LRN
        const shipment = this.dataService.currentShipment();
        if (!shipment) return null;
        const alreadyHasShipment = breadcrumbs.some(
          (b) =>
            b.itemName === 'shipments' ||
            b.itemName === 'importShipments' ||
            b.itemName === 'exportShipments' ||
            b.itemName.startsWith('#S')
        );
        if (alreadyHasShipment) return null;

        const parentTrail: Breadcrumb[] = [
          {
            id: 0,
            itemName: actualModule,
            route: `/${actualModule}`,
            showArrow: true,
          },
          {
            id: 0,
            itemName:
              actualModule === 'import' ? 'importShipments' : 'exportShipments',
            route: `/${actualModule}/shipments`,
            showArrow: true,
          },
          {
            id: 0,
            itemName: `#${shipment.shipmentNo}`,
            route: `/shipments/details/${shipment.id}/info`,
            showArrow: true,
          },
        ];
        const home = breadcrumbs[0];
        const remaining = breadcrumbs
          .slice(1)
          .filter(
            (b) =>
              b.itemName !== 'declarations' &&
              b.itemName !== 'importDeclarations' &&
              b.itemName !== 'exportDeclarations'
          );
        return [home, ...parentTrail, ...remaining];
      }
      // Add further parentLookup types here as needed:
      // case 'broker': { ... }
      // case 'client': { ... }
      default:
        return null;
    }
  }

  /**
   * Prepends the active module's root label when the user navigates laterally into
   * a different module (e.g. clicking a Shipment No. link from the Declarations list).
   * Extend this method with additional `if` blocks to support more cross-module paths.
   */
  private tryInsertCrossModuleContext(
    breadcrumbs: Breadcrumb[],
    activeModule: string,
    url: string
  ): Breadcrumb[] | null {
    if (
      (activeModule === 'import' || activeModule === 'export') &&
      url.includes('/shipments/details/')
    ) {
      const listRoute = `/${activeModule}/shipments`;
      const home = breadcrumbs[0];
      const withoutShipmentsLabel = breadcrumbs
        .slice(1)
        .filter(
          (b) =>
            b.itemName !== 'shipments' &&
            b.itemName !== 'importShipments' &&
            b.itemName !== 'exportShipments'
        );
      return [
        home,
        {
          id: 0,
          itemName:
            activeModule === 'import' ? 'importShipments' : 'exportShipments',
          route: listRoute,
          showArrow: true,
        },
        ...withoutShipmentsLabel,
      ];
    }

    if (
      (activeModule === 'import' || activeModule === 'export') &&
      url.includes('/declarations/details/')
    ) {
      // Check if we are nested under a shipment parent lookup already
      const hasShipmentParent = breadcrumbs.some(
        (b) =>
          b.itemName.startsWith('#S') ||
          b.itemName === 'shipments' ||
          b.itemName === 'importShipments' ||
          b.itemName === 'exportShipments'
      );
      if (!hasShipmentParent) {
        const listRoute = `/${activeModule}/declarations`;
        const home = breadcrumbs[0];
        const withoutDeclarationsLabel = breadcrumbs
          .slice(1)
          .filter(
            (b) =>
              b.itemName !== 'declarations' &&
              b.itemName !== 'importDeclarations' &&
              b.itemName !== 'exportDeclarations'
          );
        return [
          home,
          {
            id: 0,
            itemName:
              activeModule === 'import'
                ? 'importDeclarations'
                : 'exportDeclarations',
            route: listRoute,
            showArrow: true,
          },
          ...withoutDeclarationsLabel,
        ];
      }
    }

    if (
      activeModule === 'declarations' &&
      url.includes('/shipments/details/')
    ) {
      const landingPage = this.dataService.currentModuleLandingPage();
      const declarationsEntry: Breadcrumb = {
        id: 0,
        itemName: 'declarations',
        route:
          landingPage === 'list' ? '/declarations' : '/dashboard/declarations',
        showArrow: true,
      };
      const home = breadcrumbs[0];
      const withoutShipmentsLabel = breadcrumbs
        .slice(1)
        .filter((b) => b.itemName !== 'shipments');
      return [home, declarationsEntry, ...withoutShipmentsLabel];
    }

    // Transit declaration details: replace generic 'declarations' root with
    // transit module context so the trail reads:
    //   home › transit › transitArrival/transitDeparture › #lrn
    // When a shipment context is active (drilled in from shipment details), prepend it:
    //   home › shipments › #shipmentNo › transit › transitArrival/transitDeparture › #lrn
    if (url.includes('/transit-declarations/details/')) {
      const declaration = this.dataService.currentDeclaration();
      const procType = declaration?.procedureType;
      const isTransit =
        procType != null && ['D1', 'D2', 'D3', 'D4'].includes(procType);
      if (isTransit) {
        const transitType = (declaration as any)?.TransitInfo?.type as
          | 'ARRIVAL'
          | 'DEPARTURE'
          | undefined;
        const isArrival = transitType !== 'DEPARTURE';
        const subLabel = isArrival ? 'transitArrival' : 'transitDeparture';
        const subRoute = isArrival
          ? '/transit-declarations/transit-arrival'
          : '/transit-declarations/transit-departure';
        const home = breadcrumbs[0];
        const remaining = breadcrumbs
          .slice(1)
          .filter(
            (b) => b.itemName !== 'transit' && b.itemName !== 'transitNcts'
          );
        const transitModuleRoute = '/transit-declarations/transit-arrival';
        const transitCrumbs: Breadcrumb[] = [
          {
            id: 0,
            itemName: 'transitNcts',
            route: transitModuleRoute,
            showArrow: true,
          },
          { id: 0, itemName: subLabel, route: subRoute, showArrow: true },
          ...remaining,
        ];
        const shipment = this.dataService.currentShipment();
        if (shipment) {
          const landingPage = this.dataService.currentModuleLandingPage();
          const listRoute =
            landingPage === 'list' ? '/shipments/all' : '/dashboard/shipments';
          return [
            home,
            { id: 0, itemName: 'shipments', route: listRoute, showArrow: true },
            {
              id: 0,
              itemName: `#${shipment.shipmentNo}`,
              route: `/shipments/details/${shipment.id}/info`,
              showArrow: true,
              isDynamic: true,
            },
            ...transitCrumbs,
          ];
        }
        return [home, ...transitCrumbs];
      }
    }

    // Settings intra-entity stacking: broker / client → child detail pages.
    // When the user drills from a broker (or client) detail tab into a child detail page
    // (e.g. broker → Users tab → user details), we prepend the parent entity chain so
    // the trail reads:  home › globalModules › brokers › [BrokerName] › users › [UserName]
    if (url.includes('/settings/users/details/')) {
      const broker = this.dataService.currentBroker();
      if (broker) {
        const leading = breadcrumbs.slice(0, 2); // [home, globalModules]
        const entityEntry = breadcrumbs[breadcrumbs.length - 1]; // user's name
        return [
          ...leading,
          {
            id: 0,
            itemName: 'brokers',
            route: '/settings/brokers',
            showArrow: true,
          },
          {
            id: 0,
            itemName: broker.name,
            route: `/settings/brokers/details/${broker.id}/info`,
            showArrow: true,
            isDynamic: true,
          },
          {
            id: 0,
            itemName: 'users',
            route: `/settings/brokers/details/${broker.id}/user`,
            showArrow: true,
          },
          entityEntry,
        ];
      }
      const client = this.dataService.currentClient();
      if (client) {
        const leading = breadcrumbs.slice(0, 2);
        const entityEntry = breadcrumbs[breadcrumbs.length - 1];
        return [
          ...leading,
          {
            id: 0,
            itemName: 'clients',
            route: '/settings/clients',
            showArrow: true,
          },
          {
            id: 0,
            itemName: client.name,
            route: `/settings/clients/details/${client.id}/info`,
            showArrow: true,
            isDynamic: true,
          },
          {
            id: 0,
            itemName: 'users',
            route: `/settings/clients/details/${client.id}/user`,
            showArrow: true,
          },
          entityEntry,
        ];
      }
    }

    if (url.includes('/settings/clients/details/')) {
      const broker = this.dataService.currentBroker();
      if (broker) {
        const leading = breadcrumbs.slice(0, 2);
        const entityEntry = breadcrumbs[breadcrumbs.length - 1];
        return [
          ...leading,
          {
            id: 0,
            itemName: 'brokers',
            route: '/settings/brokers',
            showArrow: true,
          },
          {
            id: 0,
            itemName: broker.name,
            route: `/settings/brokers/details/${broker.id}/info`,
            showArrow: true,
            isDynamic: true,
          },
          {
            id: 0,
            itemName: 'clients',
            route: `/settings/brokers/details/${broker.id}/client`,
            showArrow: true,
          },
          entityEntry,
        ];
      }
    }

    /**
     * Transit template create/edit sits beside the templates list, so the URL
     * does not include the NCTS tab crumb. Insert it before New or Edit:
     * home › globalModules › templates › NCTS › new|edit
     */
    if (
      url.includes('/settings/declaration-templates/transit/edit/') ||
      url.includes('/settings/declaration-templates/transit/new')
    ) {
      if (!breadcrumbs.some((crumb) => crumb.itemName === 'transitNcts')) {
        const leaf = breadcrumbs[breadcrumbs.length - 1];
        const templateName =
          this.dataService.currentEntityName()['declarationTemplate'];
        const templateCrumb = templateName
          ? [
              {
                id: 0,
                itemName: templateName,
                route: url,
                showArrow: true,
                isDynamic: true,
              },
            ]
          : [];
        return [
          ...breadcrumbs.slice(0, -1),
          {
            id: 0,
            itemName: 'transitNcts',
            route: '/settings/declaration-templates/transit',
            showArrow: true,
          },
          ...templateCrumb,
          leaf,
        ];
      }
    }

    /**
     * On the edit page both 'declarationTemplate' (group name) and
     * 'declarationTemplateItem' (procedure type) are registered.  The service
     * inserts the group name as a navigable parent crumb pointing to the templates
     * list, while the procedure type becomes the final (current) crumb.
     */
    if (url.includes('/settings/declaration-templates/edit/')) {
      const groupName =
        this.dataService.currentEntityName()['declarationTemplate'];
      if (groupName) {
        const editCrumb = breadcrumbs[breadcrumbs.length - 1];
        if (editCrumb?.itemName === groupName) {
          return breadcrumbs;
        }
        return [
          ...breadcrumbs.slice(0, -1), // [home, globalModules, declarationTemplates]
          {
            id: 0,
            itemName: groupName,
            route: '/settings/declaration-templates/declaration',
            showArrow: true,
            isDynamic: true,
          },
          editCrumb,
        ];
      }
    }

    // Manifest create/edit opened from a declaration template:
    // templates › [TemplateName] › manifestTemplates › new|edit(name)
    if (
      url.includes('/settings/manifest-templates/new') ||
      url.includes('/settings/manifest-templates/edit/')
    ) {
      const query = this.router.parseUrl(this.router.url).queryParams;
      const declarationTemplateId = query['declarationTemplateId'];
      if (declarationTemplateId) {
        const parent =
          this.dataService.currentDeclarationTemplate() ??
          (this.dataService.currentEntityName()['declarationTemplate']
            ? {
                id: String(declarationTemplateId),
                name: this.dataService.currentEntityName()[
                  'declarationTemplate'
                ],
              }
            : null);
        if (parent?.name) {
          const leading = breadcrumbs.slice(0, 2); // [home, globalModules]
          const leaf = breadcrumbs[breadcrumbs.length - 1];
          return [
            ...leading,
            {
              id: 0,
              itemName: 'templates',
              route: '/settings/declaration-templates',
              showArrow: true,
            },
            {
              id: 0,
              itemName: parent.name,
              route: `/settings/declaration-templates/edit/${parent.id}`,
              showArrow: true,
              isDynamic: true,
            },
            {
              id: 0,
              itemName: 'manifestTemplates',
              route: `/settings/declaration-templates/edit/${parent.id}`,
              showArrow: true,
            },
            leaf,
          ];
        }
      } else {
        const leading = breadcrumbs.slice(0, 2); // [home, globalModules]
        const leaf = breadcrumbs[breadcrumbs.length - 1];
        return [
          ...leading,
          {
            id: 0,
            itemName: 'templates',
            route: '/settings/declaration-templates',
            showArrow: true,
          },
          {
            id: 0,
            itemName: 'manifestTemplates',
            route: '/settings/declaration-templates/manifest',
            showArrow: true,
          },
          leaf,
        ];
      }
    }

    // clients/edit: stacks client name into the edit-page breadcrumb trail so the
    // trail reads:  home › globalModules › clients › [ClientName] › edit
    if (url.includes('/settings/clients/edit/')) {
      const client = this.dataService.currentClient();
      if (client) {
        const editCrumb = breadcrumbs[breadcrumbs.length - 1];
        return [
          ...breadcrumbs.slice(0, 2),
          {
            id: 0,
            itemName: 'clients',
            route: '/settings/clients',
            showArrow: true,
          },
          {
            id: 0,
            itemName: client.name,
            route: `/settings/clients/details/${client.id}/info`,
            showArrow: true,
            isDynamic: true,
          },
          editCrumb,
        ];
      }
    }

    // brokers/edit: stacks broker name into the edit-page breadcrumb trail so the
    // trail reads:  home › globalModules › brokers › [BrokerName] › edit
    if (url.includes('/settings/brokers/edit/')) {
      const broker = this.dataService.currentBroker();
      if (broker) {
        const editCrumb = breadcrumbs[breadcrumbs.length - 1];
        return [
          ...breadcrumbs.slice(0, 2),
          {
            id: 0,
            itemName: 'brokers',
            route: '/settings/brokers',
            showArrow: true,
          },
          {
            id: 0,
            itemName: broker.name,
            route: `/settings/brokers/details/${broker.id}/info`,
            showArrow: true,
            isDynamic: true,
          },
          editCrumb,
        ];
      }
    }

    // parties/edit: stacks party name into the edit-page breadcrumb trail so the
    // trail reads:  home › globalModules › parties › [PartyName] › edit
    if (url.includes('/settings/parties/edit/')) {
      const partyName = this.dataService.currentEntityName()['party'];
      if (partyName) {
        const partyId = url
          .split('/parties/edit/')[1]
          ?.split('?')[0]
          ?.split('/')[0];
        const editCrumb = breadcrumbs[breadcrumbs.length - 1];
        return [
          ...breadcrumbs.slice(0, 2),
          {
            id: 0,
            itemName: 'parties',
            route: '/settings/parties',
            showArrow: true,
          },
          {
            id: 0,
            itemName: partyName,
            route: `/settings/parties/details/${partyId}/info`,
            showArrow: true,
            isDynamic: true,
          },
          editCrumb,
        ];
      }
    }

    // users/edit: stacks user name into the edit-page breadcrumb trail.
    // Handles three contexts: broker › users › user › edit,
    //                         client › users › user › edit,
    //                         users › user › edit  (standalone)
    if (url.includes('/settings/users/edit/')) {
      const broker = this.dataService.currentBroker();
      const client = this.dataService.currentClient();
      const userName = this.dataService.currentEntityName()['user'];
      const userId = url.split('/users/edit/')[1]?.split('?')[0]?.split('/')[0];
      const editCrumb = breadcrumbs[breadcrumbs.length - 1];
      if (broker && userName) {
        return [
          ...breadcrumbs.slice(0, 2),
          {
            id: 0,
            itemName: 'brokers',
            route: '/settings/brokers',
            showArrow: true,
          },
          {
            id: 0,
            itemName: broker.name,
            route: `/settings/brokers/details/${broker.id}/info`,
            showArrow: true,
            isDynamic: true,
          },
          {
            id: 0,
            itemName: 'users',
            route: `/settings/brokers/details/${broker.id}/user`,
            showArrow: true,
          },
          {
            id: 0,
            itemName: userName,
            route: `/settings/users/details/${userId}`,
            showArrow: true,
            isDynamic: true,
          },
          editCrumb,
        ];
      }
      if (client && userName) {
        return [
          ...breadcrumbs.slice(0, 2),
          {
            id: 0,
            itemName: 'clients',
            route: '/settings/clients',
            showArrow: true,
          },
          {
            id: 0,
            itemName: client.name,
            route: `/settings/clients/details/${client.id}/info`,
            showArrow: true,
            isDynamic: true,
          },
          {
            id: 0,
            itemName: 'users',
            route: `/settings/clients/details/${client.id}/user`,
            showArrow: true,
          },
          {
            id: 0,
            itemName: userName,
            route: `/settings/users/details/${userId}`,
            showArrow: true,
            isDynamic: true,
          },
          editCrumb,
        ];
      }
      if (userName) {
        return [
          ...breadcrumbs.slice(0, 2),
          {
            id: 0,
            itemName: 'users',
            route: '/settings/users',
            showArrow: true,
          },
          {
            id: 0,
            itemName: userName,
            route: `/settings/users/details/${userId}`,
            showArrow: true,
            isDynamic: true,
          },
          editCrumb,
        ];
      }
    }

    // Add further cross-module cases here as needed:
    // if (activeModule === 'globalModules' && url.includes('/shipments/details/')) { ... }

    if (url.includes('/dashboard/transit')) {
      const home = breadcrumbs[0];
      return [
        home,
        {
          id: 0,
          itemName: 'dashboard',
          route: '/dashboard/shipments',
          showArrow: true,
        },
        {
          id: 0,
          itemName: 'transitDashboard',
          route: '/dashboard/transit',
          showArrow: true,
        },
      ];
    }

    return null;
  }

  /**
   * Reads `breadcrumbParent` from the latest navigation state (footer tabs vs
   * in-page links/buttons) and stores it for RTO detail breadcrumb reshaping.
   */
  private captureBreadcrumbParentFromNavigation(): void {
    const state = this.router.lastSuccessfulNavigation()?.extras?.state as
      | Record<string, unknown>
      | undefined;
    const parent = state?.['breadcrumbParent'];
    if (typeof parent === 'string' && parent.trim() !== '') {
      this.dataService.setBreadcrumbParentModule(parent);
    }
  }

  /**
   * Rewrites RTO shipment detail trails to:
   * home › {parentModule} › #AWB
   * Parent module comes from footer-tab navigation (`rto`) or from the module
   * that owns the link/button that opened RTO details (`import` / `shipments`).
   * The redundant `rtoDetails` leaf is omitted — the user is already there.
   * @param breadcrumbs - Trail built from route config
   * @param url - Current path without query string
   * @returns Reshaped trail for RTO details, otherwise unchanged
   */
  private reshapeRtoShipmentDetailsBreadcrumbs(
    breadcrumbs: Breadcrumb[],
    url: string
  ): Breadcrumb[] {
    const match = url.match(/\/rto\/shipments\/details\/([^/?#]+)/);
    if (!match) {
      return breadcrumbs;
    }

    const shipmentId = match[1];
    const shipment = this.dataService.currentShipment();
    const shipmentNo =
      shipment?.shipmentNo != null && String(shipment.shipmentNo).trim() !== ''
        ? `#${shipment.shipmentNo}`
        : `#${shipmentId}`;

    const home = breadcrumbs[0] ?? {
      id: 0,
      itemName: 'home',
      route: '/dashboard',
      showArrow: true,
    };

    const parentModule =
      this.dataService.breadcrumbParentModule() ||
      this.dataService.activeModule() ||
      'rto';

    const parentCrumb = this.buildRtoDetailParentCrumb(parentModule);

    return [
      home,
      parentCrumb,
      {
        id: 0,
        itemName: shipmentNo,
        route: `/shipments/details/${shipmentId}/overview`,
        showArrow: true,
        isDynamic: true,
      },
    ];
  }

  /**
   * Builds the module crumb that precedes the shipment number on RTO details.
   * @param parentModule - Module id that owns the navigation entry point
   * @returns Parent breadcrumb item
   */
  private buildRtoDetailParentCrumb(parentModule: string): Breadcrumb {
    if (parentModule === 'import') {
      return {
        id: 0,
        itemName: 'importShipments',
        route: '/import/shipments',
        showArrow: true,
      };
    }
    if (parentModule === 'export') {
      return {
        id: 0,
        itemName: 'exportShipments',
        route: '/export/shipments',
        showArrow: true,
      };
    }
    if (parentModule === 'shipments') {
      return {
        id: 0,
        itemName: 'shipments',
        route: '/shipments/all',
        showArrow: true,
      };
    }
    return {
      id: 0,
      itemName: 'pgts',
      route: '/rto/shipments',
      showArrow: true,
    };
  }

  /**
   * Reshapes EMCS breadcrumbs based on the active tab/sub-view (EU Movements, Import, Export).
   *
   * @param breadcrumbs - The accumulated breadcrumb array
   * @param url - The clean URL path (without query params)
   * @returns Reshaped breadcrumb array
   */
  private reshapeEmcsBreadcrumbs(
    breadcrumbs: Breadcrumb[],
    url: string
  ): Breadcrumb[] {
    if (url !== '/emcs' && url !== '/emcs/') {
      return breadcrumbs;
    }

    const parsedUrl = this.router.parseUrl(this.router.url);
    const queryParams = parsedUrl.queryParams;
    const specificValue = (queryParams['specificValueFields'] as string) ?? '';

    let activeTabLabel = 'euMovements';
    if (
      specificValue.includes('originTypeCode') ||
      specificValue.includes('Import') ||
      specificValue.includes('IMPORT') ||
      queryParams['tab'] === 'emcs-import' ||
      queryParams['tab'] === 'import'
    ) {
      activeTabLabel = 'import';
    } else if (
      specificValue.includes('destinationTypeCode') ||
      specificValue.includes('Export') ||
      specificValue.includes('EXPORT') ||
      queryParams['tab'] === 'emcs-export' ||
      queryParams['tab'] === 'export'
    ) {
      activeTabLabel = 'export';
    }

    const currentFullUrl = this.router.url;

    const hasSubTabCrumb = breadcrumbs.some((b) =>
      ['euMovements', 'import', 'export'].includes(b.itemName)
    );

    if (hasSubTabCrumb) {
      return breadcrumbs.map((b) => {
        if (
          b.itemName === 'euMovements' ||
          b.itemName === 'import' ||
          b.itemName === 'export'
        ) {
          return {
            ...b,
            itemName: activeTabLabel,
            route: currentFullUrl,
          };
        }
        return b;
      });
    }

    // If only [home, emcs] was built, append the active tab
    const emcsIdx = breadcrumbs.findIndex((b) => b.itemName === 'emcs');
    if (emcsIdx !== -1) {
      return [
        ...breadcrumbs.slice(0, emcsIdx + 1),
        {
          id: 0,
          itemName: activeTabLabel,
          route: currentFullUrl,
          showArrow: true,
        },
        ...breadcrumbs.slice(emcsIdx + 1),
      ];
    }

    return breadcrumbs;
  }

  /**
   * Appends a deleted-list crumb for declaration / transit template list pages.
   * @param breadcrumbs - Current breadcrumb trail
   * @param url - Current path without query string
   * @returns Trail with deleted-list crumb when the active query param indicates delete view
   */
  private injectTemplatesDeletedListContext(
    breadcrumbs: Breadcrumb[],
    url: string
  ): Breadcrumb[] {
    const parsed = this.router.parseUrl(this.router.url);
    const queryParams = parsed.queryParams;

    if (url.endsWith('/declaration-templates/declaration')) {
      if (queryParams['deleted'] !== 'deleted') {
        return breadcrumbs;
      }
      return this.appendTemplatesDeletedListCrumb(
        breadcrumbs,
        'deletedDeclarationTemplatesList',
        url,
        queryParams
      );
    }

    if (url.endsWith('/declaration-templates/transit')) {
      if (queryParams['view'] !== 'deleted') {
        return breadcrumbs;
      }
      return this.appendTemplatesDeletedListCrumb(
        breadcrumbs,
        'deletedTransitTemplatesList',
        url,
        queryParams
      );
    }

    return breadcrumbs;
  }

  /**
   * Adds the deleted templates list label to the breadcrumb trail once.
   * @param breadcrumbs - Current breadcrumb trail
   * @param itemName - Translation key for the deleted list label
   * @param path - List route path
   * @param queryParams - Active query param map
   * @returns Updated breadcrumb trail
   */
  private appendTemplatesDeletedListCrumb(
    breadcrumbs: Breadcrumb[],
    itemName: string,
    path: string,
    queryParams: Record<string, string | string[] | undefined>
  ): Breadcrumb[] {
    if (breadcrumbs.some((crumb) => crumb.itemName === itemName)) {
      return breadcrumbs;
    }

    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(queryParams)) {
      if (value == null) {
        continue;
      }
      if (Array.isArray(value)) {
        value.forEach((entry) => params.append(key, entry));
      } else {
        params.set(key, value);
      }
    }
    const queryString = params.toString();
    return [
      ...breadcrumbs,
      {
        id: 0,
        itemName,
        route: queryString ? `${path}?${queryString}` : path,
        showArrow: true,
      },
    ];
  }

  /**
   * Searches the route tree for a metadata key and returns its value (or null).
   */
  private getRouteMetadataValue(
    route: ActivatedRouteSnapshot,
    key: string
  ): string | null {
    const val = route.data[key];
    if (val) return val as string;
    for (const child of route.children) {
      const result = this.getRouteMetadataValue(child, key);
      if (result) return result;
    }
    return null;
  }

  /**
   * Helper to deep-search the route tree for specific metadata (boolean check).
   */
  private checkRouteMetadata(
    route: ActivatedRouteSnapshot,
    key: string,
    value: string
  ): boolean {
    if (route.data[key] === value) return true;
    return route.children.some((child) =>
      this.checkRouteMetadata(child, key, value)
    );
  }

  /**
   * Assigns unique IDs and ensures proper formatting for the final trail.
   */
  private finalizeBreadcrumbs(breadcrumbs: Breadcrumb[]): Breadcrumb[] {
    return breadcrumbs.map((b, i) => ({
      ...b,
      id: i + 1,
      // Hide arrow for the last item
      showArrow: i < breadcrumbs.length - 1,
    }));
  }
}
